import React from 'react';
import { FormStep } from '../../../../types/ContactForm';
import './ProgressBar.scss';

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  steps?: FormStep[];
  showProgress?: boolean;
  progressBarStyle?: 'bar' | 'numbered' | 'dots' | 'minimal';
  progressBarColor?: string;
  progressBarBackgroundColor?: string;
  progressBarHeight?: number;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  currentStep,
  totalSteps,
  steps,
  showProgress,
  progressBarStyle = 'bar',
  progressBarColor = '#007cba',
  progressBarBackgroundColor = '#e9ecef',
  progressBarHeight = 12
}) => {
  const currentStepData = steps?.[currentStep - 1];
  const progressPercentage = (currentStep / totalSteps) * 100;
  
  if (!currentStepData && (!showProgress || !totalSteps || totalSteps <= 0)) {
    return null;
  }

  const renderProgressBar = () => {
    const baseBarStyle = {
      backgroundColor: progressBarBackgroundColor,
      height: `${progressBarHeight}px`
    };

    const fillStyle = {
      backgroundColor: progressBarColor,
      width: `${progressPercentage}%`
    };

    switch (progressBarStyle) {
      case 'numbered':
        return (
          <div className="numberedProgress">
            {Array.from({ length: Math.max(1, totalSteps || 1) }, (_, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber <= currentStep;
              const isCurrent = stepNumber === currentStep;
              const isCompleted = stepNumber < currentStep;
              const showConnectionLine = index < totalSteps - 1;
              
              return (
                <div key={stepNumber} className="stepContainer">
                  <div
                    className={`numberedStep ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}
                    style={{
                      backgroundColor: isActive ? progressBarColor : progressBarBackgroundColor,
                      borderColor: isActive ? progressBarColor : progressBarBackgroundColor,
                      color: isActive ? '#fff' : '#666'
                    }}
                  >
                    {isCompleted ? '' : stepNumber}
                  </div>
                  
                  {showConnectionLine && (
                    <div
                      className="connectionLine"
                      style={{
                        backgroundColor: progressBarBackgroundColor
                      }}
                    >
                      <div
                        className={`connectionLineFill ${isCompleted ? 'active' : ''}`}
                        style={{
                          backgroundColor: progressBarColor
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 'dots':
        return (
          <div className="dotsProgress">
            {Array.from({ length: Math.max(1, totalSteps || 1) }, (_, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber <= currentStep;
              
              return (
                <div
                  key={stepNumber}
                  className={`dot ${isActive ? 'active' : ''}`}
                  style={{
                    backgroundColor: isActive ? progressBarColor : progressBarBackgroundColor,
                    width: `${progressBarHeight * 2}px`,
                    height: `${progressBarHeight * 2}px`
                  }}
                />
              );
            })}
          </div>
        );

      case 'minimal':
        return (
          <div className="minimalProgress">
            <span className="currentStep" style={{ color: progressBarColor }}>
              {currentStep}
            </span>
            <span className="separator">/</span>
            <span className="totalSteps">
              {totalSteps}
            </span>
          </div>
        );

      default:
        return (
          <div className="progressBar" style={baseBarStyle}>
            <div className="progressFill" style={fillStyle} />
          </div>
        );
    }
  };

  return (
    <div className="progressContainer">
      {showProgress && totalSteps > 0 && renderProgressBar()}
      
      {currentStepData && (
        <div className="stepInfo">
          <div className="stepDetails">
            <h3 className="stepTitle">
              {currentStepData.title}
            </h3>
            {currentStepData.description && (
              <p className="stepDescription">
                {currentStepData.description}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 